/**
 * THE RECOGNITION / PURSUIT LAYER — the model behind the Police, Fire and Greek
 * spaces. Pure JS, no React, so `verify-spaces.mjs` asserts it directly.
 *
 * WHY DEALS AND NOT COMPANY PROPERTIES (Brian, 2026-08-05). Custom company
 * properties can't be created through the connector, and a recognition
 * nomination or a product pursuit is a THING THAT HAPPENS ON A DATE WITH AN
 * OWNER AND A STAGE — which is exactly what a HubSpot DEAL already is, natively,
 * with `dealname` / `hubspot_owner_id` / `dealstage` / `closedate` / `amount`.
 * So both are modelled as a deal ASSOCIATED TO THE ORGANIZATION'S COMPANY
 * record, the same way the RFP line already works. Nothing new has to be created
 * on the HubSpot side for the read to light up.
 *
 * TWO PROGRAMS, one shape:
 *
 *  - COMMUNITY HERO (police + fire). ONE nominated hero per precinct or
 *    department — a serving or retired officer/firefighter recognised for their
 *    years of service. Southern Recognition provides the ring or pendant at NO
 *    COST. The deal is named `Community Hero — <Department>` and is associated
 *    to that department's company record, so "does this department have a hero
 *    yet" is answerable by reading the association, and one-active-per-precinct
 *    is enforceable rather than aspirational.
 *  - GREEK PURSUIT (greek). One deal per PRODUCT PURSUED at a chapter or
 *    national organization: the deal name IS the product, the owner is the rep,
 *    a date records when the pursuit started and `closedate` records when it was
 *    WON. A national and its chapters are distinguished by `sr_org_type`.
 *
 * THE WRITE IS STUBBED, DELIBERATELY. This module composes the exact payload a
 * create would send and refuses to send it while `recognitionWritesEnabled()` is
 * false (the default). See `RECOGNITION_WRITE_TODO` for the one manual step that
 * turns it on. The READ side — the badge, the columns, the drawer sections — is
 * real code with no flag on it and renders the moment the data is present.
 */

/* ---- The two space kinds ------------------------------------------- */

/** Police / fire: an organization running a Community Hero recognition program. */
export const COMMUNITY_HERO = 'community-hero'
/** Greek: a chapter or national org with product pursuits tracked against it. */
export const GREEK_PURSUIT = 'greek-pursuit'

/** SR's standing commitment, stated on every nomination. Not a sales line. */
export const NO_COST_NOTE =
  'Southern Recognition provides the Community Hero ring or pendant at NO COST to the department or the honoree.'

/* ---- Hero status ---------------------------------------------------- */

/**
 * The nomination's lifecycle. `active` is what one-hero-per-precinct keys off:
 * a declined or withdrawn nomination frees the slot; every other state holds it.
 *
 * Deliberately tolerant — the deal's own stage word is whatever HubSpot's
 * pipeline calls it, so an unrecognized status is carried through and treated as
 * ACTIVE (holding the slot) rather than silently freeing a precinct that already
 * has a hero.
 */
export const HERO_STATUSES = [
  { key: 'nominated', label: 'Nominated', color: '#3f6f9a', active: true },
  { key: 'approved', label: 'Approved', color: '#a5762a', active: true },
  { key: 'in-production', label: 'In production', color: '#6e5a3a', active: true },
  { key: 'presented', label: 'Presented', color: '#2f9e5f', active: true },
  { key: 'declined', label: 'Declined', color: '#a33f4e', active: false },
  { key: 'withdrawn', label: 'Withdrawn', color: '#8b8b8b', active: false },
]

const HERO_BY_KEY = Object.fromEntries(HERO_STATUSES.map((s) => [s.key, s]))

// Every spelling a pipeline might use for the same state, mapped onto the key.
const HERO_ALIASES = {
  nominated: 'nominated', nomination: 'nominated', submitted: 'nominated',
  new: 'nominated', open: 'nominated', appointmentscheduled: 'nominated',
  approved: 'approved', accepted: 'approved', qualified: 'approved',
  qualifiedtobuy: 'approved',
  'in production': 'in-production', production: 'in-production',
  'in-production': 'in-production', manufacturing: 'in-production',
  presented: 'presented', awarded: 'presented', delivered: 'presented',
  closedwon: 'presented', 'closed won': 'presented', won: 'presented',
  declined: 'declined', rejected: 'declined', closedlost: 'declined',
  'closed lost': 'declined', lost: 'declined',
  withdrawn: 'withdrawn', cancelled: 'withdrawn', canceled: 'withdrawn',
}

const NO_HERO = { key: '', label: '', color: 'var(--faint)', active: false, present: false }

/** Normalize any status / dealstage word onto a hero status entry. */
export function heroStatusFor(raw) {
  const s = String(raw ?? '').trim()
  if (!s) return HERO_BY_KEY.nominated
  const key = HERO_ALIASES[s.toLowerCase()]
  if (key) return HERO_BY_KEY[key]
  // Unknown stage: keep the pipeline's own word and hold the slot. Freeing a
  // precinct on a word we don't recognize would let a second hero be nominated.
  return { key: 'other', label: s, color: '#3f6f9a', active: true }
}

/**
 * The hero badge for one organization record — `{ present, label, color,
 * active, name }`. `present: false` is the honest "no hero nominated yet"
 * state and is what the badge column renders as a gap.
 */
export function heroMeta(record) {
  const hero = record?.hero
  if (!hero) return NO_HERO
  const st = heroStatusFor(hero.status || hero.stage)
  return {
    present: true,
    key: st.key,
    label: st.label,
    color: st.color,
    active: st.active,
    name: String(hero.name || '').trim(),
    years: typeof hero.yearsOfService === 'number' ? hero.yearsOfService : null,
  }
}

/**
 * ONE ACTIVE HERO PER PRECINCT — enforced in the UI, and stated as a reason
 * rather than a disabled button with no explanation.
 *
 * This is a UX gate, not enforcement: the same rule has to be re-decided
 * server-side when the write route ships (see `RECOGNITION_WRITE_TODO`), the way
 * every other client check in this platform is treated (CLAUDE.md hard rule 5).
 */
export function canNominateHero(record) {
  if (!record) return { allowed: false, reason: 'No record selected.' }
  const meta = heroMeta(record)
  if (meta.present && meta.active) {
    const who = meta.name ? ` — ${meta.name}` : ''
    return {
      allowed: false,
      reason: `${record.name} already has an active Community Hero${who} (${meta.label}). One hero per precinct or department; withdraw or decline the current nomination before nominating another.`,
    }
  }
  if (meta.present && !meta.active) {
    return {
      allowed: true,
      reason: `The previous nomination is ${meta.label.toLowerCase()}, so the slot is open.`,
    }
  }
  return { allowed: true, reason: '' }
}

/* ---- Greek pursuit -------------------------------------------------- */

/** A national organization outranks its chapters and is grouped above them. */
const NATIONAL_ORG = /(national|international|headquarter|grand chapter|inc\.?$)/i

/**
 * Is this Greek record the NATIONAL organization rather than a chapter?
 *
 * `sr_org_type` is the recorded answer and always wins. The name test is only a
 * fallback for records whose org type isn't set yet, and it is deliberately
 * narrow — a false "national" would sort a chapter to the top of the board.
 */
export function isNationalOrg(record) {
  const t = String(record?.orgType || '').trim()
  if (t) return /national|headquarter|grand/i.test(t) && !/chapter/i.test(t)
  return NATIONAL_ORG.test(String(record?.name || ''))
}

/** `'national'` / `'chapter'` — the group a Greek record sorts into. */
export function orgTier(record) {
  return isNationalOrg(record) ? 'national' : 'chapter'
}

/** Nationals first, then chapters; stable within a group. */
export function nationalsFirst(records) {
  const nat = [], chap = []
  for (const r of records || []) (isNationalOrg(r) ? nat : chap).push(r)
  return [...nat, ...chap]
}

/**
 * The pursuit status for a Greek record — `{ present, label, color, won, lost }`.
 * A won pursuit is what puts a real WON DATE on the row; nothing derives one.
 */
export function pursuitDealMeta(record) {
  const p = record?.pursuitDeal
  if (!p) return { present: false, label: '', color: 'var(--faint)', won: false, lost: false }
  const raw = String(p.status || p.stage || '').trim()
  const lower = raw.toLowerCase()
  const won = p.won === true || /won/.test(lower)
  const lost = p.lost === true || /lost|declined/.test(lower)
  return {
    present: true,
    label: raw || (won ? 'Won' : 'In pursuit'),
    color: won ? '#2f9e5f' : lost ? '#a33f4e' : '#8a5db8',
    won,
    lost,
  }
}

/* ---- The create payloads (composed here, sent by nobody yet) --------- */

const ymd = (d) =>
  d instanceof Date && !Number.isNaN(d.getTime())
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : ''

/** The deal name for a department's hero nomination — one canonical spelling. */
export function heroDealName(record) {
  return `Community Hero — ${String(record?.name || '').trim() || 'Unnamed department'}`
}

/**
 * The payload a Community Hero nomination WOULD post.
 *
 * It carries BUSINESS names only — no HubSpot pipeline id, no `dealstage`
 * internal value, no property id. The client has no way to know those and
 * inventing one would write a deal into the wrong pipeline; the backend route
 * resolves them (see `RECOGNITION_WRITE_TODO`). `companyId` is the RAW HubSpot
 * company id the association is made against, not the namespaced record id.
 */
export function heroDealPayload(record, { hero = {}, owner = '', ownerId = '' } = {}) {
  return {
    kind: COMMUNITY_HERO,
    line: record?.lineKey || '',
    companyId: record?.sourceId || '',
    organization: record?.name || '',
    dealname: heroDealName(record),
    status: HERO_BY_KEY.nominated.label,
    owner: String(owner || '').trim(),
    ownerId: String(ownerId || '').trim(),
    hero: {
      name: String(hero.name || '').trim(),
      rank: String(hero.rank || '').trim(),
      yearsOfService: hero.yearsOfService === '' || hero.yearsOfService == null
        ? null
        : Number(hero.yearsOfService),
      citation: String(hero.citation || '').trim(),
    },
    note: NO_COST_NOTE,
  }
}

/**
 * The payload a Greek pursuit WOULD post. The deal NAME is the product pursued —
 * that is the model Brian specified, so a chapter's board reads as the list of
 * things we are actually going after there.
 */
export function pursuitDealPayload(record, { product = '', owner = '', ownerId = '', pursuitDate = null } = {}) {
  const name = String(product || '').trim()
  return {
    kind: GREEK_PURSUIT,
    line: record?.lineKey || '',
    companyId: record?.sourceId || '',
    organization: record?.name || '',
    orgType: record?.orgType || '',
    dealname: name,
    product: name,
    status: 'To Pursue',
    owner: String(owner || '').trim(),
    ownerId: String(ownerId || '').trim(),
    pursuitDate: pursuitDate instanceof Date ? ymd(pursuitDate) : String(pursuitDate || ''),
  }
}

/** Everything missing before a payload could be sent — [] when it is complete. */
export function payloadProblems(payload) {
  const out = []
  if (!payload?.companyId) out.push('no HubSpot company id on the record — the deal has nothing to associate to')
  if (!payload?.dealname) out.push('no deal name')
  if (payload?.kind === COMMUNITY_HERO && !payload?.hero?.name) out.push('no honoree name')
  if (payload?.kind === GREEK_PURSUIT && !payload?.product) out.push('no product selected')
  return out
}

/* ---- The write ------------------------------------------------------- */

/**
 * THE WRITE IS LIVE. `POST /api/recognition-deal` shipped on sr-portal-backend
 * and the `?recog=1` gate that stood in for it is gone — a rep clicking
 * Nominate creates a real HubSpot deal, associated to the department.
 *
 * What the backend re-decides, because a client check is never enforcement
 * (hard rule 5) and `canNominateHero` above is UX only:
 *
 *  - ONE ACTIVE HERO PER PRECINCT → `409 hero_conflict`, carrying the deal that
 *    holds the slot. The guard fails CLOSED: if the check itself can't run, the
 *    create is refused rather than allowed through.
 *  - IDEMPOTENCY → a re-submitted nomination returns the existing deal with
 *    `created: false` instead of a second one.
 *  - CREATES ONLY → the route has no update path. Advancing a nomination or
 *    marking a pursuit won stays a human action on a human-owned deal, which is
 *    why the guided pathway's later steps LINK to HubSpot instead of writing.
 *
 * The response the modal renders:
 *   201 `{ ok, created: true,  id, dealname, associated, droppedProperties }`
 *   200 `{ ok, created: false, duplicate: true, id, message }`
 *   409 `{ ok: false, error: 'hero_conflict', conflict: { id, dealname, heroName, stage } }`
 */
export const RECOGNITION_WRITE_ROUTE = '/api/recognition-deal'

/**
 * The record patch that reflects a just-created deal, so the badge / pursuit row
 * updates the moment the create returns instead of waiting on a refetch.
 *
 * Deliberately minimal and deliberately HONEST: it records exactly what we know
 * we just wrote (the honoree, the product, the owner, the id) and nothing we
 * don't. The stage is left as the nomination head — the real pipeline stage
 * comes back on the next load, and inventing one here would put a word on the
 * badge that HubSpot never said.
 */
export function optimisticPatch(kind, payload, result) {
  const id = String(result?.id || '')
  if (kind === COMMUNITY_HERO) {
    return {
      hero: {
        id,
        dealname: payload?.dealname || '',
        name: payload?.hero?.name || '',
        rank: payload?.hero?.rank || '',
        status: HERO_BY_KEY.nominated.label,
        stage: HERO_BY_KEY.nominated.label,
        owner: payload?.owner || '',
        ownerId: payload?.ownerId || '',
        yearsOfService: typeof payload?.hero?.yearsOfService === 'number'
          ? payload.hero.yearsOfService
          : null,
        nominatedAt: new Date(),
        presentedAt: null,
      },
      heroCount: 1,
    }
  }
  return {
    pursuitDeal: {
      id,
      dealname: payload?.dealname || '',
      product: payload?.product || '',
      status: payload?.status || 'To Pursue',
      stage: payload?.status || 'To Pursue',
      owner: payload?.owner || '',
      ownerId: payload?.ownerId || '',
      won: false,
      lost: false,
      pursuitDate: payload?.pursuitDate ? new Date(`${payload.pursuitDate}T00:00:00`) : new Date(),
      wonDate: null,
      amount: null,
    },
    pursuitCount: 1,
    product: payload?.product || '',
    rep: payload?.owner || '',
    pursuitDate: payload?.pursuitDate ? new Date(`${payload.pursuitDate}T00:00:00`) : new Date(),
  }
}

/** The product lines a Greek pursuit can be opened for. */
export const GREEK_PRODUCTS = [
  'Badges & Insignia',
  'Chapter Rings',
  'Recognition Jewelry',
  'Awards & Plaques',
  'Founders Day Commemoratives',
  'Officer Recognition',
  'Alumni / Legacy Program',
]
